import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  Grid,
  FormControl,
  FormControlLabel,
  Checkbox,
  TextField
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { RentalProperty } from '../pages/BorrowerForm';
import HookFormTextField from './HookFormTextField';

interface PropertyAccordionProps {
  propertyId: string;
  propertyData: RentalProperty;
  expanded: boolean;
  onChange: (propertyId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  onRemoveProperty: (propertyId: string) => void;
  handlePropertyChange: (propertyId: string, field: keyof RentalProperty, value: string | boolean) => void;
  StyledFormControl: typeof FormControl;
}

const PropertyAccordion: React.FC<PropertyAccordionProps> = ({
  propertyId,
  propertyData,
  expanded,
  onChange,
  onRemoveProperty,
  handlePropertyChange,
  StyledFormControl
}) => {
  // Handler for checkbox changes
  const handleCheckboxChange = (field: keyof RentalProperty) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handlePropertyChange(propertyId, field, event.target.checked);
  };

  // Handler for text field changes
  const handleTextChange = (field: keyof RentalProperty) => (value: string) => {
    handlePropertyChange(propertyId, field, value);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange(propertyId)}
      sx={{ mb: 0.5, backgroundColor: 'rgba(240, 245, 250, 0.9)' }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${propertyId}-content`}
        id={`${propertyId}-header`}
        sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)', minHeight: '42px', '& .MuiAccordionSummary-content': { margin: '8px 0' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <Typography>{`Property ${propertyData.address ? ': ' + propertyData.address : ''}`}</Typography>
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onRemoveProperty(propertyId);
            }}
            sx={{ mr: 2 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ 
        pt: 1, 
        pb: 0.5,
        maxHeight: '400px',
        overflow: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        msOverflowStyle: 'none'
      }}>
        <Grid container spacing={0.5}>
          {/* Property Type Checkboxes */} 
          <Grid item xs={12} container spacing={0.3} sx={{ mb: 0.3 }}>
            <Grid item xs={6} sm={3} md={1.7}>
              <FormControl component="fieldset" sx={{ display: 'flex', alignItems: 'center', height: '28px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={propertyData.propertyType === 'Rental'}
                    onChange={() => handlePropertyChange(propertyId, 'propertyType', 'Rental')} 
                    value="Rental" 
                    size="small"
                  />
                  <Typography variant="body2">Rental</Typography>
                </Box>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.7}>
              <FormControl component="fieldset" sx={{ display: 'flex', alignItems: 'center', height: '28px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={propertyData.propertyType === 'O/O + Rental'}
                    onChange={() => handlePropertyChange(propertyId, 'propertyType', 'O/O + Rental')} 
                    value="O/O + Rental" 
                    size="small"
                  />
                  <Typography variant="body2">O/O + Rental</Typography>
                </Box>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.7}>
              <FormControl component="fieldset" sx={{ display: 'flex', alignItems: 'center', height: '28px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={propertyData.propertyType === 'Subject rental'}
                    onChange={() => handlePropertyChange(propertyId, 'propertyType', 'Subject rental')} 
                    value="Subject rental" 
                    size="small"
                  />
                  <Typography variant="body2">Subject rental</Typography>
                </Box>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.7}>
              <FormControl component="fieldset" sx={{ display: 'flex', alignItems: 'center', height: '28px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={propertyData.propertyType === 'Subject O/O'}
                    onChange={() => handlePropertyChange(propertyId, 'propertyType', 'Subject O/O')}
                    value="Subject O/O"
                    size="small"
                  />
                  <Typography variant="body2">Subject O/O</Typography>
                </Box>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.7}>
              <FormControl component="fieldset" sx={{ display: 'flex', alignItems: 'center', height: '28px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={propertyData.propertyType === 'Subject 2nd Home'}
                    onChange={() => handlePropertyChange(propertyId, 'propertyType', 'Subject 2nd Home')}
                    value="Subject 2nd Home"
                    size="small"
                  />
                  <Typography variant="body2">Subject 2nd Home</Typography>
                </Box>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.7}>
              <FormControl component="fieldset" sx={{ display: 'flex', alignItems: 'center', height: '28px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={propertyData.propertyType === 'Second Home'}
                    onChange={() => handlePropertyChange(propertyId, 'propertyType', 'Second Home')}
                    value="Second Home"
                    size="small"
                  />
                  <Typography variant="body2">Second Home</Typography>
                </Box>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.7}>
              <FormControl component="fieldset" sx={{ display: 'flex', alignItems: 'center', height: '28px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={propertyData.propertyType === 'Subject O/O + Rental'}
                    onChange={() => handlePropertyChange(propertyId, 'propertyType', 'Subject O/O + Rental')}
                    value="Subject O/O + Rental"
                    size="small"
                  />
                  <Typography variant="body2">Subject O/O + Rental</Typography>
                </Box>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.7}> 
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(propertyData.newBuild)}
                    onChange={handleCheckboxChange('newBuild')}
                    name="newBuild"
                    size="small"
                  />
                }
                label="New Build"
                sx={{ my: 0, height: '28px' }}
              />
            </Grid>
          </Grid>
          
          {/* Basic Property Information */}
          <Grid item xs={12} sm={6} md={1.7}>
            <HookFormTextField
              fieldId={`${propertyId}-address`}
              name="address"
              label="Address"
              value={propertyData.address}
              onChange={handleTextChange('address')}
              InputProps={{
                sx: { height: '28px' }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4} md={1.7}>
            <HookFormTextField
              fieldId={`${propertyId}-purchasePrice`}
              name="purchasePrice"
              label="Purchase Price"
              value={propertyData.purchasePrice}
              onChange={handleTextChange('purchasePrice')}
              InputProps={{
                startAdornment: <span>$</span>,
                sx: { height: '28px' }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4} md={1.7}>
            <HookFormTextField
              fieldId={`${propertyId}-rentalIncome`}
              name="rentalIncome"
              label="Rental Income"
              value={propertyData.rentalIncome}
              onChange={handleTextChange('rentalIncome')}
              InputProps={{
                startAdornment: <span>$</span>,
                sx: { height: '28px' }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4} md={1.7}>
            <HookFormTextField
              fieldId={`${propertyId}-propertyTax`}
              name="propertyTax"
              label="Property Tax"
              value={propertyData.propertyTax}
              onChange={handleTextChange('propertyTax')}
              InputProps={{
                startAdornment: <span>$</span>,
                sx: { height: '28px' }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4} md={1.7}>
            <HookFormTextField
              fieldId={`${propertyId}-condoFees`}
              name="condoFees"
              label="Condo Fees"
              value={propertyData.condoFees}
              onChange={handleTextChange('condoFees')}
              InputProps={{
                startAdornment: <span>$</span>,
                sx: { height: '28px' }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4} md={1.7}>
            <HookFormTextField
              fieldId={`${propertyId}-mortgagePayment`}
              name="mortgagePayment"
              label="Mortgage Payment"
              value={propertyData.mortgagePayment}
              onChange={handleTextChange('mortgagePayment')}
              InputProps={{
                startAdornment: <span>$</span>,
                sx: { height: '28px' }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4} md={1.7}>
            <HookFormTextField
              fieldId={`${propertyId}-mortgageAmount`}
              name="mortgageAmount"
              label="Mortgage Amount"
              value={propertyData.mortgageAmount}
              onChange={handleTextChange('mortgageAmount')}
              InputProps={{
                startAdornment: <span>$</span>,
                sx: { height: '28px' }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4} md={1.7}>
            <HookFormTextField
              fieldId={`${propertyId}-heat`}
              name="heat"
              label="Heat"
              value={propertyData.heat}
              onChange={handleTextChange('heat')}
              InputProps={{
                startAdornment: <span>$</span>,
                sx: { height: '28px' }
              }}
            />
          </Grid>
          
          {/* Additional Checkboxes */}
          <Grid item xs={12} container spacing={0.3} sx={{ mt: 0.3 }}>
            <Grid item xs={6} sm={4} md={1.7}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(propertyData.addThreePercent)}
                    onChange={handleCheckboxChange('addThreePercent')}
                    name="addThreePercent"
                    size="small"
                  />
                }
                label="+3% Tax"
                sx={{ my: 0, height: '28px' }}
              />
            </Grid>
            
            <Grid item xs={6} sm={4} md={1.7}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(propertyData.addOnePercentPP)}
                    onChange={handleCheckboxChange('addOnePercentPP')}
                    name="addOnePercentPP"
                    size="small"
                  />
                }
                label="+1% PP"
                sx={{ my: 0, height: '28px' }}
              />
            </Grid>
            
            <Grid item xs={6} sm={4} md={1.7}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(propertyData.calculateSurplus)}
                    onChange={handleCheckboxChange('calculateSurplus')}
                    name="calculateSurplus"
                    size="small"
                  />
                }
                label="Calculate Surplus"
                sx={{ my: 0, height: '28px' }}
              />
            </Grid>
            
            <Grid item xs={6} sm={4} md={1.7}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(propertyData.useNinetyFivePercentIncome)}
                    onChange={handleCheckboxChange('useNinetyFivePercentIncome')}
                    name="useNinetyFivePercentIncome"
                    size="small"
                  />
                }
                label="95% Income"
                sx={{ my: 0, height: '28px' }}
              />
            </Grid>
          </Grid>
          
          {/* Ownership Percentages */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Ownership Percentages</Typography>
          </Grid>
          
          <Grid item xs={6} sm={3} md={1.7}>
            <HookFormTextField
              fieldId={`${propertyId}-ownedPercentB1`}
              name="ownedPercentB1"
              label="Borrower 1 %"
              value={propertyData.ownedPercentB1}
              onChange={handleTextChange('ownedPercentB1')}
              InputProps={{
                sx: { height: '28px' }
              }}
            />
          </Grid>
          
          <Grid item xs={6} sm={3} md={1.7}>
            <HookFormTextField
              fieldId={`${propertyId}-ownedPercentB2`}
              name="ownedPercentB2"
              label="Borrower 2 %"
              value={propertyData.ownedPercentB2}
              onChange={handleTextChange('ownedPercentB2')}
              InputProps={{
                sx: { height: '28px' }
              }}
            />
          </Grid>
          
          <Grid item xs={6} sm={3} md={1.7}>
            <HookFormTextField
              fieldId={`${propertyId}-ownedPercentB3`}
              name="ownedPercentB3"
              label="Borrower 3 %"
              value={propertyData.ownedPercentB3}
              onChange={handleTextChange('ownedPercentB3')}
              InputProps={{
                sx: { height: '28px' }
              }}
            />
          </Grid>
          
          <Grid item xs={6} sm={3} md={1.7}>
            <HookFormTextField
              fieldId={`${propertyId}-ownedPercentB4`}
              name="ownedPercentB4"
              label="Borrower 4 %"
              value={propertyData.ownedPercentB4}
              onChange={handleTextChange('ownedPercentB4')}
              InputProps={{
                sx: { height: '28px' }
              }}
            />
          </Grid>
          
          {/* Additional Property Flags */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Property Flags</Typography>
          </Grid>
          
          <Grid item xs={6} sm={4} md={1.7}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(propertyData.marketRent)}
                  onChange={handleCheckboxChange('marketRent')}
                  name="marketRent"
                  size="small"
                />
              }
              label="Market Rent"
              sx={{ my: 0, height: '28px' }}
            />
          </Grid>
          
          <Grid item xs={6} sm={4} md={1.7}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(propertyData.leaseAgreement)}
                  onChange={handleCheckboxChange('leaseAgreement')}
                  name="leaseAgreement"
                  size="small"
                />
              }
              label="Lease Agreement"
              sx={{ my: 0, height: '28px' }}
            />
          </Grid>
          
          <Grid item xs={6} sm={4} md={1.7}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(propertyData.noticeToVacate)}
                  onChange={handleCheckboxChange('noticeToVacate')}
                  name="noticeToVacate"
                  size="small"
                />
              }
              label="Notice to Vacate"
              sx={{ my: 0, height: '28px' }}
            />
          </Grid>
          
          <Grid item xs={6} sm={4} md={1.7}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(propertyData.beingSoldForDP)}
                  onChange={handleCheckboxChange('beingSoldForDP')}
                  name="beingSoldForDP"
                  size="small"
                />
              }
              label="Being Sold for DP"
              sx={{ my: 0, height: '28px' }}
            />
          </Grid>
          
          <Grid item xs={6} sm={4} md={1.7}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(propertyData.refiForDP)}
                  onChange={handleCheckboxChange('refiForDP')}
                  name="refiForDP"
                  size="small"
                />
              }
              label="Refi for DP"
              sx={{ my: 0, height: '28px' }}
            />
          </Grid>
          
          <Grid item xs={6} sm={4} md={1.7}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(propertyData.bridgeAB)}
                  onChange={handleCheckboxChange('bridgeAB')}
                  name="bridgeAB"
                  size="small"
                />
              }
              label="Bridge AB"
              sx={{ my: 0, height: '28px' }}
            />
          </Grid>
          
          <Grid item xs={6} sm={4} md={1.7}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(propertyData.bridgeBC)}
                  onChange={handleCheckboxChange('bridgeBC')}
                  name="bridgeBC"
                  size="small"
                />
              }
              label="Bridge BC"
              sx={{ my: 0, height: '28px' }}
            />
          </Grid>
          
          <Grid item xs={6} sm={4} md={1.7}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(propertyData.outOfProvince)}
                  onChange={handleCheckboxChange('outOfProvince')}
                  name="outOfProvince"
                  size="small"
                />
              }
              label="Out of Province"
              sx={{ my: 0, height: '28px' }}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

function areRentalPropertiesEqual(prevData: RentalProperty, nextData: RentalProperty): boolean {
  const keys1 = Object.keys(prevData) as Array<keyof RentalProperty>;
  const keys2 = Object.keys(nextData) as Array<keyof RentalProperty>;

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (prevData[key] !== nextData[key]) {
      return false;
    }
  }
  return true;
}

function areAccordionPropsEqual(prevProps: PropertyAccordionProps, nextProps: PropertyAccordionProps): boolean {
  // Check primitive props first
  if (
    prevProps.propertyId !== nextProps.propertyId ||
    prevProps.expanded !== nextProps.expanded ||
    // Check function references (should be stable due to useCallback in parent)
    prevProps.onChange !== nextProps.onChange ||
    prevProps.onRemoveProperty !== nextProps.onRemoveProperty ||
    prevProps.handlePropertyChange !== nextProps.handlePropertyChange ||
    prevProps.StyledFormControl !== nextProps.StyledFormControl
  ) {
    return false;
  }

  // Deep compare propertyData
  if (!areRentalPropertiesEqual(prevProps.propertyData, nextProps.propertyData)) {
    return false;
  }

  return true;
}

export default React.memo(PropertyAccordion, areAccordionPropsEqual);
